export const ARCHIVES_FAQ = [
    {
        id: "Q1",
        question: "Who were the Girmitiyas?",
        answer:
            "Girmitiyas were Indian indentured labourers taken to colonies under contractual systems after the abolition of slavery.",
    },
    {
        id: "Q2",
        question: "When did the indenture system begin and end?",
        answer:
            "The indenture system began in 1834 following the abolition of slavery in British colonies and officially ended in 1920 after sustained pressure from Indian nationalists and reformers.",
    },
    {
        id: "Q3",
        question: "Which countries did Girmitiyas go to?",
        answer:
            "Girmitiyas were transported to Mauritius, Trinidad & Tobago, Guyana, Suriname, Fiji, South Africa, Jamaica, and several other British, Dutch, and French colonies across the Caribbean, Pacific, and Indian Ocean.",
    },
    {
        id: "Q4",
        question: "What was the 'girmit' contract?",
        answer:
            "The word 'girmit' is a local pronunciation of the English word 'agreement.' The contract bound labourers for a fixed term, usually five years, to work on plantations in exchange for wages, food, and return passage, though these promises were frequently not honoured.",
    },
    {
        id: "Q5",
        question: "How did Girmitiyas preserve their culture?",
        answer:
            "Despite displacement and harsh conditions, Girmitiyas kept their culture alive through language (Hindi, Bhojpuri, Tamil), religious festivals, music, food, and oral traditions. Over generations, these evolved into distinct Indo-diaspora cultures unique to each country.",
    },
];

export const ARCHIVE_SECTIONS = [
    {
        id: "2.1",
        slug: "documents",
        title: "Documents Archive",
        description:
            "Original indenture contracts, government records, plantation registers, and migration documents, all preserved and accessible in digital form.",
        items: [
            "Indenture Contracts",
            "Government Notifications",
            "Plantation Records",
            "Immigration Registers",
        ],
        category: "Documents Archive",
    },
    {
        id: "2.2",
        slug: "photographs",
        title: "Photographs & Visual Archive",
        description:
            "Rare photographs of ships, plantations, settlements, and family archives, a visual record of the Girmitiya journey.",
        items: ["Ships", "Plantations", "Early Settlements", "Family Archives"],
        category: "Photographs & Visual Archive",
    },
    {
        id: "2.3",
        slug: "newspapers",
        title: "Newspapers & Journals",
        description:
            "Diaspora newspapers and journals documenting community voices, a record of the lived experiences, struggles, and achievements of the Girmitiya community.",
        items: [
            "Hindi / Bhojpuri / English publications",
            "Diaspora newspapers and journals",
        ],
        category: "Newspapers & Journals",
    },
    {
        id: "2.4",
        slug: "oral-history",
        title: "Oral History",
        description:
            "This section documents oral histories of Girmitiya descendants, preserving lived experiences and generational memories.",
        items: [
            "Recorded Interviews",
            "Transcripts (Text)",
            "Audio / Video Clips",
            "Country-wise Filter",
        ],
        category: "Oral History",
    },
    {
        id: "2.5",
        slug: "ships-routes",
        title: "Ships & Routes",
        description:
            "A dedicated record of indenture-era vessels and the migration pathways that carried Girmitiya labourers across the oceans.",
        subsections: [
            {
                id: "5.1",
                title: "Ships Database",
                fields: [
                    "Ship Name",
                    "Year",
                    "Port of Departure (India)",
                    "Port of Arrival",
                    "Number of Labourers",
                ],
            },
            {
                id: "5.2",
                title: "Indenture Routes (Map-based)",
                routes: [
                    "India → Mauritius",
                    "India → Suriname",
                    "India → Guyana",
                    "India → Fiji",
                    "India → Trinidad",
                ],
            },
        ],
        category: "Ships & Routes",
    },
    {
        id: "2.6",
        slug: "research",
        title: "Research & Publications",
        description:
            "Academic and scholarly materials related to the Girmitiya legacy.",
        items: [
            "Research Papers",
            "Theses & Dissertations",
            "Conference Proceedings",
            "Policy & Cultural Notes",
        ],
        category: "Research & Publications",
    },
];

export const ARCHIVE_CATEGORIES = [
    "All Collections",
    ...ARCHIVE_SECTIONS.map((s) => s.category),
];

export const ARCHIVES_DB = [
    {
        id: "REC-1838-012",
        title: "Gladstone Indenture Contract",
        category: "Documents Archive",
        year: "1838",
        country: "British Guiana",
        format: "Deed",
        image: "/archives/gallery-01.webp",
        description:
            "A standard 5-year 'Agreement for Labor' signed under the Gladstone experimental system. Demonstrates the strict penal clauses that defined the early indenture period.",
        curatorNote: "The thumbprint on the lower right belongs to a laborer named 'Ramdin'.",
        tags: ["Gladstone", "Legal", "Penal Clauses"],
    },
    {
        id: "REC-1879-001",
        title: "Leonidas Passenger Manifest",
        category: "Documents Archive",
        year: "1879",
        country: "Fiji",
        format: "Document",
        image: "/archives/gallery-02.webp",
        description:
            "The original passenger manifest of the ship Leonidas, which carried the first 463 Girmitiyas from Calcutta to Fiji. Features thumbprints, village origins, and assigned caste classifications.",
        curatorNote: "Restored in 2021. Note the specific regional clustering from the United Provinces.",
        tags: ["First Journey", "Calcutta Port", "Leonidas"],
    },
    // Photographs & Visual Archive
    {
        id: "REC-1882-001",
        title: "Early Settlers in Levuka, Fiji",
        category: "Photographs & Visual Archive",
        year: "1882",
        country: "Fiji",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive1.webp",
        description: "An early black-and-white group portrait of Girmitiya families gathered outside their thatched-roof bures in Levuka, showcasing the early community structure.",
        curatorNote: "One of the earliest photographic records of family life in the Pacific indentured settlement.",
        tags: ["Levuka", "Family Life", "Settlement"],
    },
    {
        id: "REC-1894-002",
        title: "Calcutta Depot Departure",
        category: "Photographs & Visual Archive",
        year: "1894",
        country: "India / Mauritius",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive2.webp",
        description: "Indentured labourers lining up at the Calcutta migration depot before boarding ships for Mauritius. Features medical inspection officers and British agents.",
        curatorNote: "Depot photos are rare; this one shows the scale of departure in the late 19th century.",
        tags: ["Calcutta", "Depot", "Departure"],
    },
    {
        id: "REC-1905-003",
        title: "Fiji Sugar Cane Field Hands",
        category: "Photographs & Visual Archive",
        year: "1905",
        country: "Fiji",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive3.webp",
        description: "A rare photograph capturing Indian labourers working under heavy supervision in the sugar cane fields of the Colonial Sugar Refining (CSR) Company.",
        curatorNote: "Documents the intense labor conditions and tools used during the peak CSR era.",
        tags: ["CSR Company", "Laborers", "Agriculture"],
    },
    {
        id: "REC-1898-004",
        title: "Trinidad Barrack-Style Quarters",
        category: "Photographs & Visual Archive",
        year: "1898",
        country: "Trinidad & Tobago",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive4.webp",
        description: "The long wooden barrack ranges provided to indentured families on the sugar estates of Trinidad, capturing the communal yet confined living conditions.",
        curatorNote: "Provides architectural context for early Indo-Caribbean settlements.",
        tags: ["Barracks", "Living Conditions", "Estate"],
    },
    {
        id: "REC-1912-005",
        title: "Indo-Surinamese Family Portrait",
        category: "Photographs & Visual Archive",
        year: "1912",
        country: "Suriname",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive5.webp",
        description: "A three-generation portrait of an Indo-Surinamese family in traditional attire, celebrating a wedding ceremony after completing their indenture term.",
        curatorNote: "Demonstrates the transition from indentured status to permanent free settlers.",
        tags: ["Suriname", "Family Portrait", "Culture"],
    },
    {
        id: "REC-1908-006",
        title: "Loading Sugar Cane in Natal",
        category: "Photographs & Visual Archive",
        year: "1908",
        country: "South Africa",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive6.webp",
        description: "Labourers operating early mechanical cranes to load harvested sugar cane onto railway wagons on a plantation near Durban.",
        curatorNote: "Highlights the integration of industrialization with indentured labor.",
        tags: ["Durban", "Natal", "Sugar Cane"],
    },
    {
        id: "REC-1875-007",
        title: "Port Louis Harbour Landing",
        category: "Photographs & Visual Archive",
        year: "1875",
        country: "Mauritius",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive7.webp",
        description: "Historical depiction of a small boat transporting newly arrived Girmitiyas from a larger sailing vessel to the Aapravasi Ghat steps in Port Louis.",
        curatorNote: "An iconic visual representing the physical arrival point of over half a million immigrants.",
        tags: ["Port Louis", "Aapravasi Ghat", "Arrival"],
    },
    {
        id: "REC-1915-008",
        title: "Bhojpuri Musical Instruments",
        category: "Photographs & Visual Archive",
        year: "1915",
        country: "Fiji",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive8.webp",
        description: "A photograph of early community musicians holding traditional instruments such as the dholak, harmonium, and kartal, used during evening bhajan sessions.",
        curatorNote: "Music was central to cultural resilience and community preservation in the lines.",
        tags: ["Music", "Resilience", "Bhajans"],
    },
    {
        id: "REC-1890-009",
        title: "Calcutta Emigration Registers",
        category: "Photographs & Visual Archive",
        year: "1890",
        country: "India",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive9.webp",
        description: "A visual record of the large register books stored at the Calcutta port office, documenting ship passenger lists and individual descriptions.",
        curatorNote: "Indispensable resource for modern genealogical tracing.",
        tags: ["Registers", "Calcutta", "Genealogy"],
    },
    {
        id: "REC-1902-010",
        title: "Indentured Women in Trinidad",
        category: "Photographs & Visual Archive",
        year: "1902",
        country: "Trinidad & Tobago",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive10.webp",
        description: "A rare photograph focusing on two Indian women in traditional silver jewelry and hand-woven garments on a cocoa estate in Trinidad.",
        curatorNote: "Highlights women's active economic and cultural role in the diaspora.",
        tags: ["Women", "Jewelry", "Trinidad"],
    },
    {
        id: "REC-1914-011",
        title: "Suriname Plantation Schoolroom",
        category: "Photographs & Visual Archive",
        year: "1914",
        country: "Suriname",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive11.webp",
        description: "Children of indentured and free labourers attending a basic classroom school run by local missionaries on a plantation estate.",
        curatorNote: "Shows the early efforts toward educational access for the descendants of Girmitiyas.",
        tags: ["Education", "Children", "Plantation"],
    },
    {
        id: "REC-1865-012",
        title: "Aapravasi Ghat Arrival Depot",
        category: "Photographs & Visual Archive",
        year: "1865",
        country: "Mauritius",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive12.webp",
        description: "A high-quality historical photograph of the stone buildings and steps of Aapravasi Ghat in Port Louis, where immigration records were processed.",
        curatorNote: "A UNESCO World Heritage site and a symbol of the global indentured labor system.",
        tags: ["Aapravasi Ghat", "Mauritius", "Depot"],
    },
    {
        id: "REC-1909-013",
        title: "Fiji Plantation Hospital Ward",
        category: "Photographs & Visual Archive",
        year: "1909",
        country: "Fiji",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive13.webp",
        description: "Inside a basic plantation infirmary facility showing the medical treatment layout and beds provided for injured or sick field workers.",
        curatorNote: "Plantation healthcare was highly criticized by contemporary reformers.",
        tags: ["Healthcare", "Fiji", "Plantation Life"],
    },
    {
        id: "REC-1888-014",
        title: "Labourers on a Sailing Ship",
        category: "Photographs & Visual Archive",
        year: "1888",
        country: "Global",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive14.webp",
        description: "A historic view from the deck of a sailing ship, showing indentured passengers sitting together in the open air during their long journey.",
        curatorNote: "Captures the daily existence and close-quarters 'Jahaji Bhai' camaraderie on board.",
        tags: ["Jahaji Bhai", "Ship Deck", "Voyage"],
    },
    {
        id: "REC-1896-015",
        title: "Panchayat Assembly under Banyan",
        category: "Photographs & Visual Archive",
        year: "1896",
        country: "Suriname",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive15.webp",
        description: "Community elders gathering under a large tree to form an informal panchayat court to resolve local disputes and govern community customs.",
        curatorNote: "Illustrates the self-governing social institutions established by Girmitiyas.",
        tags: ["Panchayat", "Elders", "Community"],
    },
    {
        id: "REC-1922-016",
        title: "First Generation Free Farmers",
        category: "Photographs & Visual Archive",
        year: "1922",
        country: "Mauritius",
        format: "Photograph",
        image: "/archives/photographsArchive/PhotoArchive16.webp",
        description: "Former indentured labourers posing on their own purchased plots of land, signifying the successful transition to independent agriculture.",
        curatorNote: "Illustrates the impact of land acquisition on the socioeconomic status of descendants.",
        tags: ["Free Farmers", "Land Ownership", "Post-Indenture"],
    },
    // Newspapers & Journals
    {
        id: "REC-1907-001",
        title: "The Mauritius Indian Patriot",
        category: "Newspapers & Journals",
        year: "1907",
        country: "Mauritius",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive.webp",
        description: "A rare front-page issue of the influential bilingual newspaper fighting for political representation, civil rights, and educational reforms for the Indo-Mauritian community.",
        curatorNote: "Founded by progressive leaders, this newspaper played a key role in raising political consciousness.",
        tags: ["Mauritius", "Political Rights", "Early Press"],
    },
    {
        id: "REC-1909-002",
        title: "Bande Mataram Diaspora Column",
        category: "Newspapers & Journals",
        year: "1909",
        country: "India / South Africa",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive2.webp",
        description: "An editorial column in the Bande Mataram journal highlighting the struggle of South African Indians under Gandhi's passive resistance campaign.",
        curatorNote: "Highlights the intersection of Indian nationalism and diaspora struggles.",
        tags: ["Nationalism", "Gandhi", "Satyagraha"],
    },
    {
        id: "REC-1927-003",
        title: "Fiji Samachar Inaugural Issue",
        category: "Newspapers & Journals",
        year: "1927",
        country: "Fiji",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive3.webp",
        description: "A historic copy of Fiji Samachar, one of the earliest Hindi-language newspapers in the Pacific, detailing community achievements and cultural gatherings.",
        curatorNote: "Crucial for tracing the development of Fiji Hindi literary tradition.",
        tags: ["Fiji Samachar", "Hindi Press", "Pacific"],
    },
    {
        id: "REC-1931-004",
        title: "The East Indian Weekly",
        category: "Newspapers & Journals",
        year: "1931",
        country: "Trinidad & Tobago",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive4.webp",
        description: "A complete issue of the weekly journal catering to the Indo-Trinidadian intellectual circle, featuring literary essays, social critiques, and local news.",
        curatorNote: "A rich archive showing the emerging distinct Indo-Caribbean identity.",
        tags: ["Trinidad", "Intellectual History", "Weekly Journal"],
    },
    {
        id: "REC-1923-007",
        title: "Dwarka Prasad's Fiji Editorials",
        category: "Newspapers & Journals",
        year: "1923",
        country: "Fiji",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive7.webp",
        description: "Clippings of critical editorials demanding equal wages and better sanitary conditions on plantation quarters following the 1920 strikes.",
        curatorNote: "Captures the post-indenture labor union movements in the colony.",
        tags: ["Labor Unions", "Strikes", "Fiji"],
    },
    {
        id: "REC-1913-008",
        title: "The Indian Opinion: Great March",
        category: "Newspapers & Journals",
        year: "1913",
        country: "South Africa",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive8.webp",
        description: "A special edition of Mahatma Gandhi's weekly Indian Opinion, covering the historic Great March of indentured coal miners across the Transvaal border.",
        curatorNote: "Indispensable document for studying the passive resistance movement.",
        tags: ["Gandhi", "Indian Opinion", "Coal Strike"],
    },
    {
        id: "REC-1918-009",
        title: "De Surinaamsche Post",
        category: "Newspapers & Journals",
        year: "1918",
        country: "Suriname",
        format: "Newsprint",
        image: "/archives/NewsArchive/NewsArchive9.webp",
        description: "A copy of a Dutch-language publication with a dedicated Hindustani community section detailing local religious festivals and immigration arrivals.",
        curatorNote: "Highlights the bilingual social environment of the Indo-Surinamese community.",
        tags: ["Suriname", "Hindustani", "Integration"],
    },
    {
        id: "REC-1895-008",
        title: "Diary of Munshi Rahman",
        category: "Oral History",
        year: "1895",
        country: "Trinidad & Tobago",
        format: "Manuscript",
        image: "/archives/gallery-03.webp",
        description:
            "Three surviving pages from a personal diary written in Awadhi/Bhojpuri script. Details the daily rations and the emotional toll of ship brotherhood separation.",
        curatorNote: "Translation currently pending. Fragile condition.",
        tags: ["Ship Brotherhood", "Awadhi", "Daily Life"],
    },
    {
        id: "REC-SHIP-1834",
        title: "The Pioneer Ship: Atlas",
        category: "Ships & Routes",
        year: "1834",
        country: "Mauritius",
        format: "Vessel Registry",
        image: "/archives/ShipsRoutesArchive/atlas.webp",
        description: "The historical record of the Atlas, the pioneer ship that arrived in Mauritius in 1834 carrying the first officially contracted Indian indentured labourers.",
        curatorNote: "Marked the formal commencement of the Great Experiment.",
        tags: ["Atlas", "Mauritius", "Pioneer Voyage"],
    },
    {
        id: "REC-SHIP-1838",
        title: "The Voyage of the Whitby",
        category: "Ships & Routes",
        year: "1838",
        country: "Guyana",
        format: "Vessel Registry",
        image: "/archives/ShipsRoutesArchive/whitby.webp",
        description: "The vessel registry and passenger summary of the Whitby, one of the two initial ships carrying Girmitiyas to the shores of British Guiana.",
        curatorNote: "Initiated the indenture system in the Caribbean region.",
        tags: ["Whitby", "Guyana", "Caribbean Landing"],
    },
    {
        id: "REC-SHIP-1845",
        title: "Fatel Razack: Arrival in Trinidad",
        category: "Ships & Routes",
        year: "1845",
        country: "Trinidad & Tobago",
        format: "Vessel Registry",
        image: "/archives/ShipsRoutesArchive/Fatel Razack.webp",
        description: "The legendary passenger ship Fatel Razack (originally Fath Al Razack), which landed at Port of Spain in 1845 with 217 pioneering passengers.",
        curatorNote: "May 30th is now celebrated annually in Trinidad as Indian Arrival Day.",
        tags: ["Fatel Razack", "Trinidad", "Arrival Day"],
    },
    {
        id: "REC-SHIP-1860",
        title: "The Truro: Voyage to Natal",
        category: "Ships & Routes",
        year: "1860",
        country: "South Africa",
        format: "Vessel Registry",
        image: "/archives/ShipsRoutesArchive/turbo.webp",
        description: "The log and visual registry of the Truro, which sailed from Madras and dropped anchor in Durban harbor, carrying the first indentured workers to South Africa.",
        curatorNote: "Durban is now home to one of the largest Indian-origin populations outside India.",
        tags: ["Truro", "Natal", "South Africa"],
    },
    {
        id: "REC-SHIP-1879",
        title: "The Leonidas: Historic Crossing to Fiji",
        category: "Ships & Routes",
        year: "1879",
        country: "Fiji",
        format: "Vessel Registry",
        image: "/archives/ShipsRoutesArchive/LeonidasShip.webp",
        description: "An authentic depiction of the Leonidas, the infamous ship that completed a challenging journey to Fiji, carrying the first wave of 463 Girmitiyas.",
        curatorNote: "The beginning of the indenture system in the Pacific islands.",
        tags: ["Leonidas", "Fiji", "Pacific Voyage"],
    },
    {
        id: "REC-SHIP-1873-1",
        title: "Lalla Rookh: Landing in Suriname",
        category: "Ships & Routes",
        year: "1873",
        country: "Suriname",
        format: "Vessel Registry",
        image: "/archives/ShipsRoutesArchive/Lalla Rookh.webp",
        description: "Records showing the sailing path and arrival details of the Lalla Rookh, the first ship to bring Indian labourers from Calcutta to Paramaribo.",
        curatorNote: "June 5th marks the official arrival celebration in Suriname.",
        tags: ["Lalla Rookh", "Suriname", "Paramaribo"],
    },
    {
        id: "REC-SHIP-1873-2",
        title: "Lalla Rookh: Vessel Ledger Details",
        category: "Ships & Routes",
        year: "1873",
        country: "Suriname",
        format: "Vessel Ledger",
        image: "/archives/ShipsRoutesArchive/Lalla Rookh image 2.webp",
        description: "Alternative journal logs and navigational details concerning the Lalla Rookh's historic crossing under challenging weather conditions.",
        curatorNote: "Illustrates the maritime conditions encountered during the Caribbean journeys.",
        tags: ["Lalla Rookh", "Maritime Log", "Suriname"],
    },
    {
        id: "REC-2019-014",
        title: "Demographic Shifts in the Global Indian Diaspora",
        category: "Research & Publications",
        year: "2019",
        country: "Global",
        format: "Report",
        image: "/archives/gallery-04.webp",
        description:
            "A contemporary demographic analysis mapping the socioeconomic progression of the Girmitiya diaspora from the 19th century to the modern era.",
        curatorNote: "Part of the Council's academic publications series.",
        tags: ["Demographics", "Research", "Diaspora"],
    },
];

export const SAMPLE_SHIPS = [
    { name: "Atlas", year: "1834", departure: "Calcutta", arrival: "Port Louis, Mauritius", labourers: "36" },
    { name: "Whitby", year: "1838", departure: "Calcutta", arrival: "Berbice, Guyana", labourers: "249" },
    { name: "Fatel Razack", year: "1845", departure: "Calcutta", arrival: "Port of Spain, Trinidad", labourers: "217" },
    { name: "Truro", year: "1860", departure: "Madras", arrival: "Durban, Natal", labourers: "342" },
    { name: "Leonidas", year: "1879", departure: "Calcutta", arrival: "Levuka, Fiji", labourers: "463" },
    { name: "Lalla Rookh", year: "1873", departure: "Calcutta", arrival: "Paramaribo, Suriname", labourers: "336" },
];
